'use client';
import React, { useState } from 'react';
import { MapPin, Clock, ChevronDown, ChevronUp, ExternalLink, Zap, Gift, Truck, Store } from 'lucide-react';
import type { Chain } from './directoryData';

const typeConfig = {
  restaurant: { icon: Store, label: 'Restaurant', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  delivery: { icon: Truck, label: 'Delivery', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  'gift-card': { icon: Gift, label: 'Gift Cards', color: 'text-btcgold-400', bg: 'bg-btcgold-500/10' },
};

const dealStatusConfig = {
  active: { label: 'Active Deal', className: 'badge-active' },
  limited: { label: 'Limited Locations', className: 'badge-warning' },
  varies: { label: 'Varies by Location', className: 'badge-inactive' },
};

const methodLabels: Record<string, string> = {
  flexa: 'Flexa',
  fold: 'Fold',
  bakkt: 'Bakkt',
  bitpay: 'BitPay',
  lightning: 'Lightning',
  bitcoin: 'Bitcoin',
  coinsbee: 'CoinsBee',
  gyft: 'Gyft',
};

interface ChainCardProps {
  chain: Chain;
}

export default function ChainCard({ chain }: ChainCardProps) {
  const [expanded, setExpanded] = useState(false);
  const typeInfo = typeConfig[chain.type];
  const TypeIcon = typeInfo.icon;
  const dealInfo = dealStatusConfig[chain.dealStatus];

  const isSheetz = chain.id === 'chain-sheetz';

  return (
    <div
      className={`rounded-xl border flex flex-col transition-all duration-200 hover:border-zinc-700 ${
        isSheetz
          ? 'border-btcgold-500/30 bg-btcgold-500/5' :'border-zinc-800 bg-card'
      }`}
    >
      {/* Card header */}
      <div className="p-5 flex flex-col gap-4">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* Logo placeholder */}
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
              style={{ backgroundColor: chain.logoColor + '22', border: `1px solid ${chain.logoColor}44` }}
            >
              <span style={{ color: chain.logoColor }} className="font-bold text-base">
                {chain.name.charAt(0)}
              </span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white leading-tight">{chain.name}</h3>
              {chain.since && (
                <p className="text-xs text-zinc-500 mt-0.5">Since {chain.since}</p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <span className={dealInfo.className}>{dealInfo.label}</span>
            <div className={`flex items-center gap-1 text-xs font-medium ${typeInfo.color}`}>
              <TypeIcon size={11} />
              {typeInfo.label}
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-zinc-400 leading-relaxed">{chain.description}</p>

        {/* Deal highlight */}
        {chain.dealTitle && (
          <div className={`rounded-lg p-3 ${isSheetz ? 'bg-btcgold-500/10 border border-btcgold-500/20' : 'bg-zinc-800/60 border border-zinc-700'}`}>
            <div className="flex items-start gap-2">
              <Zap size={14} className={isSheetz ? 'text-btcgold-400 mt-0.5 flex-shrink-0' : 'text-green-400 mt-0.5 flex-shrink-0'} />
              <div>
                <p className={`text-xs font-semibold ${isSheetz ? 'text-btcgold-300' : 'text-white'}`}>
                  {chain.dealTitle}
                  {chain.discount && (
                    <span className="ml-2 text-green-400">{chain.discount}</span>
                  )}
                </p>
                {chain.dealHours && (
                  <div className="flex items-center gap-1 mt-1">
                    <Clock size={11} className="text-zinc-500" />
                    <span className="text-xs text-zinc-400">{chain.dealHours}</span>
                  </div>
                )}
                {chain.dealMaxDiscount && (
                  <p className="text-xs text-zinc-500 mt-0.5">{chain.dealMaxDiscount}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment method badges */}
        <div className="flex flex-wrap gap-1.5">
          {chain.paymentMethods.map((method) => (
            <span key={`${chain.id}-method-${method}`} className="method-badge">
              {methodLabels[method] ?? method}
            </span>
          ))}
        </div>

        {/* Locations */}
        {chain.locations && (
          <div className="flex items-center gap-1.5">
            <MapPin size={12} className="text-zinc-500" />
            <span className="text-xs text-zinc-500">{chain.locations}</span>
          </div>
        )}
      </div>

      {/* Expandable detail */}
      {expanded && (
        <div className="px-5 pb-4 border-t border-zinc-800 pt-4 flex flex-col gap-3 animate-fade-in">
          {chain.dealDetail && (
            <div>
              <p className="text-xs font-medium text-zinc-400 mb-1">How it works</p>
              <p className="text-xs text-zinc-400 leading-relaxed">{chain.dealDetail}</p>
            </div>
          )}
          {chain.acceptedTokens && (
            <div>
              <p className="text-xs font-medium text-zinc-400 mb-1">Accepted Tokens</p>
              <p className="text-xs text-zinc-300">{chain.acceptedTokens}</p>
            </div>
          )}
          {chain.requiredApp.length > 0 && (
            <div>
              <p className="text-xs font-medium text-zinc-400 mb-1.5">Required App</p>
              <div className="flex flex-wrap gap-1.5">
                {chain.requiredApp.map((app) => (
                  <span key={`${chain.id}-app-${app}`} className="px-2 py-0.5 rounded bg-zinc-800 border border-zinc-700 text-xs text-zinc-300">
                    {app}
                  </span>
                ))}
              </div>
            </div>
          )}
          <a
            href={chain.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-btcgold-400 hover:text-btcgold-300 transition-colors w-fit"
          >
            <ExternalLink size={12} />
            Visit {chain.name}
          </a>
        </div>
      )}

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-center gap-1.5 py-3 border-t border-zinc-800 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/40 transition-all duration-150 rounded-b-xl"
      >
        {expanded ? (
          <>
            <ChevronUp size={14} />
            Show less
          </>
        ) : (
          <>
            <ChevronDown size={14} />
            View details &amp; how to pay
          </>
        )}
      </button>
    </div>
  );
}